<!--
  navItem „Kennzahlen" — die fünf KPI-Kacheln des Abrechnungskontos.
  Jede Kachel ist ein <mvs-widget>; es gibt hier bewusst keine handgebaute KPI-Karte.
  Layout ausschließlich über PrimeFlex, Farben ausschließlich über Theme-Tokens.
-->
<div class="cp-provision-agent-kpi">

    @if (!initialized) {

        <p-skeleton height="10rem" styleClass="mb-3"></p-skeleton>
        <p-skeleton height="18rem"></p-skeleton>

    } @else if (!provisionAgentId) {

        <p-message severity="info" text="Kein Abrechnungskonto ausgewählt." styleClass="w-full"></p-message>

    } @else {

        <!-- Zwischenstand-Hinweis: solange ein cp-Lauf arbeitet, sind die Zahlen unterwegs. -->
        @if (jobRunning) {
            <p-message severity="info" styleClass="w-full mb-3">
                <span>
                    <i class="fa-regular fa-circle-notch fa-spin mr-2"></i>
                    Ein Provisionslauf arbeitet gerade — die Kennzahlen zeigen einen Zwischenstand
                    und werden nach dem Lauf automatisch aktualisiert.
                </span>
            </p-message>
        }

        <div class="grid">

            <!-- Kachel 5: Deckungsquote (aktuell) — Live-Stand aus dem Coverage-Endpoint -->
            <div class="col-12 xl:col-6">
                @if (coverageLiveFailed) {
                    <p-message severity="warn" styleClass="w-full">
                        <span>
                            <i class="fa-regular fa-triangle-exclamation mr-2"></i>
                            Die aktuelle Deckung konnte nicht geladen werden.
                        </span>
                    </p-message>
                } @else if (coverageLiveWidget) {

                    <!--
                      Unterdeckungshinweis (U-03, die Auszahlungsbremse). Der Rücklagensaldo DARF
                      negativ sein; er ist dann aber die wichtigste Aussage der Kachel und muss vor
                      einem Auszahlungslauf gesehen werden — ein Minuszeichen in einer Reihe aus
                      vier Beträgen liest man zu leicht.
                    -->
                    @if (reserveNegative) {
                        <p-message severity="error" styleClass="w-full mb-2">
                            <span>
                                <i class="fa-regular fa-triangle-exclamation mr-2"></i>
                                <strong>Rücklagenkonto im Minus.</strong>
                                Auszahlungen an diesen Berater werden dadurch gebremst.
                            </span>
                        </p-message>
                    } @else if (shortfallOpen) {
                        <p-message severity="warn" styleClass="w-full mb-2">
                            <span>
                                <i class="fa-regular fa-circle-exclamation mr-2"></i>
                                <strong>Offene Haftung nicht vollständig gedeckt.</strong>
                                Die Rücklage reicht derzeit nicht für den gesamten Haftungsbestand.
                            </span>
                        </p-message>
                    }

                    <mvs-widget [widgetData]="coverageLiveWidget"></mvs-widget>
                }
            </div>

            <!-- Kachel 3: Rücklagenkonto — realer und simulierter Saldo -->
            <div class="col-12 xl:col-6">
                @if (balanceMissing) {
                    <p-message severity="info"
                               text="Für dieses Abrechnungskonto wurden noch keine Salden berechnet."
                               styleClass="w-full"></p-message>
                } @else if (reserveBalanceWidget) {
                    <mvs-widget [widgetData]="reserveBalanceWidget"></mvs-widget>
                }
            </div>

            <!-- Kachel 1: Provisionen pro Monat -->
            <div class="col-12 xl:col-6">
                @if (provisionMonthChartWidget) {
                    @if (chartEmpty.provisions) {
                        <div class="surface-card border-1 border-200 border-round-lg p-3">
                            <div class="text-base font-medium mb-2">Provisionen pro Monat</div>
                            <p-message severity="secondary" styleClass="w-full">
                                <span>
                                    <i class="fa-regular fa-chart-column mr-2"></i>
                                    Keine Daten — für diesen Berater gibt es noch keine aktiven Provisionsbuchungen. Berechnete Buchungen erscheinen hier erst nach dem Aktivieren des Laufs.
                                </span>
                            </p-message>
                        </div>
                    }
                    <!-- Hidden, not removed: the mounted widget reports a later reload through onLoadData. -->
                    <div [class.hidden]="chartEmpty.provisions">
                        <mvs-widget [widgetData]="provisionMonthChartWidget"
                                    (onLoadData)="handleChartLoaded('provisions', $event)"></mvs-widget>
                    </div>
                }
            </div>

            <!-- Kachel 2: Auszahlungen pro Monat -->
            <div class="col-12 xl:col-6">
                @if (payoutMonthChartWidget) {
                    @if (chartEmpty.payouts) {
                        <div class="surface-card border-1 border-200 border-round-lg p-3">
                            <div class="text-base font-medium mb-2">Auszahlungen pro Monat</div>
                            <p-message severity="secondary" styleClass="w-full">
                                <span>
                                    <i class="fa-regular fa-chart-column mr-2"></i>
                                    Keine Daten — an diesen Berater wurde noch nichts ausgezahlt.
                                </span>
                            </p-message>
                        </div>
                    }
                    <!-- Hidden, not removed: the mounted widget reports a later reload through onLoadData. -->
                    <div [class.hidden]="chartEmpty.payouts">
                        <mvs-widget [widgetData]="payoutMonthChartWidget"
                                    (onLoadData)="handleChartLoaded('payouts', $event)"></mvs-widget>
                    </div>
                }
            </div>

            <!-- Kachel 4: Rücklage vs. Haftung im Verlauf — Verlaufsgrafik und Zahlen je Lauf -->
            <div class="col-12 xl:col-6">
                @if (coverageHistoryChartWidget) {
                    @if (chartEmpty.coverage) {
                        <div class="surface-card border-1 border-200 border-round-lg p-3">
                            <div class="text-base font-medium mb-2">Unterdeckung je Provisionslauf</div>
                            <p-message severity="secondary" styleClass="w-full">
                                <span>
                                    <i class="fa-regular fa-chart-column mr-2"></i>
                                    Keine Daten — für dieses Abrechnungskonto liegen noch keine Deckungsstände je Lauf vor.
                                </span>
                            </p-message>
                        </div>
                    }
                    <!-- Hidden, not removed: the mounted widget reports a later reload through onLoadData. -->
                    <div [class.hidden]="chartEmpty.coverage">
                        <mvs-widget [widgetData]="coverageHistoryChartWidget"
                                    (onLoadData)="handleChartLoaded('coverage', $event)"></mvs-widget>
                    </div>
                }
            </div>

            <div class="col-12 xl:col-6">
                @if (coverageHistoryTableWidget) {
                    <mvs-widget #coverageHistoryTableRef
                                [widgetData]="coverageHistoryTableWidget"></mvs-widget>
                }
            </div>

        </div>

    }

</div>
